import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import moment from "moment";
import "../Package/Package.css"; // Ensure this path is correct
import airlinesData from "../../Data/airlines.json";
import airportsData from "../../Data/AirportList.json";

// --- Helper Functions (From Reference) ---

const getSavedFlightFingerprint = (flight) => {
  if (!flight?.flightNumber || !flight?.departureDateTime || !flight?.arrivalDateTime) return null;
  const departureISO = new Date(flight.departureDateTime).toISOString();
  const arrivalISO = new Date(flight.arrivalDateTime).toISOString();
  return `${flight.flightNumber}-${departureISO}-${arrivalISO}`;
};

const getLiveFlightFingerprint = (liveLeg) => {
  const displayLeg = liveLeg?.dcDisplayLegs?.[0];
  if (!displayLeg?.airlineData?.flightNumber || !displayLeg?.startSegment?.departureDateTime || !displayLeg?.endSegment?.arrivalDateTime) return null;
  const flightNumber = `${displayLeg.airlineData.airlineCode}-${displayLeg.airlineData.flightNumber}`;
  const departureTime = new Date(displayLeg.startSegment.departureDateTime).toISOString();
  const arrivalTime = new Date(displayLeg.endSegment.arrivalDateTime).toISOString();
  return `${flightNumber}-${departureTime}-${arrivalTime}`;
};

const getCleanCityName = (airport) => {
  if (!airport || !airport.cityName) return '';
  return airport.cityName.split(',')[0];
};

const extractAirportCode = (cityString) => {
  if (!cityString || !cityString.includes('(')) return cityString;
  const match = cityString.match(/\((.*?)\)/);
  return match ? match[1] : cityString;
};

// --- Change Flight Modal ---
const ChangeFlightModal = ({ show, onHide, flights, isLoading, onSelect, currentlySelectedFlight }) => {
  const flightLegs = flights && flights.length > 0 ? flights[0].dcFlightLegs : [];
  const savedFlightFingerprint = getSavedFlightFingerprint(currentlySelectedFlight);

  function getTimeFromMins(mins) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered dialogClassName="package-flight-modal">
      <Modal.Header closeButton><Modal.Title><i className="ri-plane-line me-2"></i>Change Flight</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="table-responsive" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Table className="flight-results-table">
            <tbody>
              {isLoading && <tr><td className="text-center p-5"><Spinner animation="border" /><p className="mt-2 mb-0">Searching for alternative flights...</p></td></tr>}
              {!isLoading && flightLegs.length > 0 ? (
                flightLegs.map((leg, index) => {
                  const displayLeg = leg?.dcDisplayLegs?.[0];
                  if (!displayLeg) return null;
                  const cheapestFare = leg.dcPriceDetails.reduce((min, curr) => (curr.totalPrice < min.totalPrice ? curr : min), leg.dcPriceDetails[0]);
                  const isSelected = savedFlightFingerprint === getLiveFlightFingerprint(leg);

                  return (
                    <tr key={index} className="flight-card-row">
                      <td className="flight-card-cell">
                        <div className="flight-card-container">
                          <div className="flight-col airline-info">
                            <img src={`/assets/images/airlinelogos/${displayLeg.airlineData?.airlineCode}.png`} alt="" className="airline-logo" onError={(e) => e.target.style.display = 'none'} />
                            <div><div className="airline-name">{displayLeg.airlineData?.airline}</div><div className="flight-number text-muted">{displayLeg.airlineData?.airlineCode}-{displayLeg.airlineData?.flightNumber}</div></div>
                          </div>
                          <div className="flight-col time-info">
                              <div className="time">{moment(displayLeg.startSegment?.departureDateTime).format("HH:mm")}</div>
                              <div className="city-code">{displayLeg.startSegment?.cityCode}</div>
                              <div className="date text-muted">{moment(displayLeg.startSegment?.departureDateTime).format("DD MMM YY")}</div>
                          </div>
                          <div className="flight-col duration-info">
                              <div className="duration">{getTimeFromMins(displayLeg.totalDuration)}</div>
                              <div className="stops-line"></div>
                              <div className="stops-text text-muted">{displayLeg.stops === 0 ? "Non-stop" : `${displayLeg.stops} Stop(s)`}</div>
                          </div>
                          <div className="flight-col time-info">
                              <div className="time">{moment(displayLeg.endSegment?.arrivalDateTime).format("HH:mm")}</div>
                              <div className="city-code">{displayLeg.endSegment?.cityCode}</div>
                              <div className="date text-muted">{moment(displayLeg.endSegment?.arrivalDateTime).format("DD MMM YY")}</div>
                          </div>
                          <div className="flight-col fare-info"><div className="price">INR {cheapestFare?.totalPrice.toFixed(2)}</div></div>
                          <div className="flight-col action-info"><Button variant={isSelected ? "success" : "primary"} size="sm" onClick={() => onSelect(leg)}>{isSelected ? "SELECTED" : "SELECT"}</Button></div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (!isLoading && <tr><td className="text-center p-4"><h5>No alternative flights found.</h5></td></tr>)}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
    </Modal>
  );
};

// --- MAIN COMPONENT ---
const HolidayFlightBooking = ({ holidayId, packageDetails, onUpdate }) => {
  // Use "temp-id" logic for initial state, will be replaced by UUID or Index
  const initialFieldValues = {
    packageFlightBookingId: "", 
    airline: "", 
    flightNumber: "", 
    departureCity: "", 
    arrivalCity: "",
    departureDateTime: "", 
    arrivalDateTime: "", 
    cabinClass: "Economy", 
    price: ""
  };

  const { user } = useAuth();
  const [lists, setLists] = useState([]);
  const [values, setValues] = useState(initialFieldValues);
  const [errors, setErrors] = useState({});
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  
  // Modal & Edit State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showChangeFlightModal, setShowChangeFlightModal] = useState(false);
  const [alternativeFlights, setAlternativeFlights] = useState([]);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [flightToUpdate, setFlightToUpdate] = useState(null);
  const [currentlySelectedFlightInModal, setCurrentlySelectedFlightInModal] = useState(null);

  // Autocomplete State
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false);
  const [departureSuggestions, setDepartureSuggestions] = useState([]);
  const [arrivalSuggestions, setArrivalSuggestions] = useState([]);
  
  const airlineInputWrapperRef = useRef(null);
  const departureInputRef = useRef(null);
  const arrivalInputRef = useRef(null);

  // 1. Load and Parse Data from PackageDetails Prop
  useEffect(() => {
    if (packageDetails && packageDetails.holidayFlight) {
        try {
            const parsed = typeof packageDetails.holidayFlight === 'string' 
                ? JSON.parse(packageDetails.holidayFlight) 
                : packageDetails.holidayFlight;
            
            // Map to ensure consistent CamelCase for frontend
            const mapped = parsed.map((f, index) => ({
                packageFlightBookingId: f.packageFlightBookingId || f.PackageFlightBookingId || `temp-${index}`,
                airline: f.airline || f.Airline,
                flightNumber: f.flightNumber || f.FlightNumber,
                departureCity: f.departureCity || f.DepartureCity,
                arrivalCity: f.arrivalCity || f.ArrivalCity,
                departureDateTime: f.departureDateTime || f.DepartureDateTime,
                arrivalDateTime: f.arrivalDateTime || f.ArrivalDateTime,
                cabinClass: f.cabinClass || f.CabinClass,
                price: f.price || f.Price
            }));
            setLists(mapped);
        } catch (e) {
            console.error("JSON Parse Error", e);
            setLists([]);
        }
    } else {
        setLists([]);
    }
  }, [packageDetails]);

  // Click Outside Listener for Autocomplete
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (airlineInputWrapperRef.current && !airlineInputWrapperRef.current.contains(event.target)) setIsSuggestionsVisible(false);
      if (departureInputRef.current && !departureInputRef.current.contains(event.target)) setDepartureSuggestions([]);
      if (arrivalInputRef.current && !arrivalInputRef.current.contains(event.target)) setArrivalSuggestions([]);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- SAVE TO BACKEND (Core Difference: Updates JSON Field) ---
  const saveHolidayData = async (updatedList) => {
      try {
        const formData = new FormData();
        formData.append("holidayId", holidayId);
        formData.append("holidayFlight", JSON.stringify(updatedList)); 
        
        // Include userId if backend expects it to track who updated
        if(user?.id) formData.append("userId", user.id);

        await apiService.update(config.UPDATEHOLIDAY, formData);
        
        if(onUpdate) {
            onUpdate(); // Trigger parent refresh
        } else {
            // If no parent refresh, manually update local state to reflect what we sent
            setLists(updatedList);
        }
        return true;
      } catch (error) {
        console.error("Save failed", error);
        toast.error("Failed to update holiday data");
        return false;
      }
  };

  // --- HANDLERS ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnLoading(true);
      
      // Generate ID for new items if needed
      const currentId = values.packageFlightBookingId || crypto.randomUUID();
      const newFlightObj = { ...values, packageFlightBookingId: currentId };

      let newList = [...lists];
      const index = newList.findIndex(x => x.packageFlightBookingId === currentId);

      if (index !== -1) {
          // Update existing
          newList[index] = newFlightObj;
      } else {
          // Add new
          newList.push(newFlightObj);
      }

      const success = await saveHolidayData(newList);
      if(success) {
          setIsFormVisible(false);
          resetForm();
         
      }
      setBtnLoading(false);
    }
  };

  const handleDelete = async () => {
      if(itemToDelete) {
          const newList = lists.filter(x => x.packageFlightBookingId !== itemToDelete);
          const success = await saveHolidayData(newList);
          if(success) 
          setShowDeleteModal(false);
          setItemToDelete(null);
      }
  };

  // --- CHANGE FLIGHT LOGIC (Adapted) ---
  const handleChangeFlightClick = async (flightBooking) => {
    setFlightToUpdate(flightBooking);
    setCurrentlySelectedFlightInModal(flightBooking);
    setShowChangeFlightModal(true);
    setIsModalLoading(true);
    setAlternativeFlights([]);

    try {
      // Logic borrowed from Reference - hits Flight Search API
      const flightSearchRequest = {
        adults: packageDetails?.adults || 1,
        kids: packageDetails?.kids || 0,
        infants: packageDetails?.infants || 0,
        journeyType: "ONEWAY",
        cabinClass: flightBooking?.cabinClass || "ECONOMY",
        isDomestic: "DOMESTIC",
        isDirectFlight: false,
        preferred: "REGULAR",
        companyId: user?.cid || 1, // Fallback if no CID
        flightTravelerPoints: [{
          from: extractAirportCode(flightBooking.departureCity),
          to: extractAirportCode(flightBooking.arrivalCity),
          departureDate: moment(flightBooking.departureDateTime).format("YYYY-MM-DD"),
          returnDate: moment(flightBooking.departureDateTime).format("YYYY-MM-DD"), // Oneway same date
        }]
      };
      
      const res = await apiService.post(config.SEARCHFLIGHTALTERNATIVES, flightSearchRequest);
      setAlternativeFlights(res);
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not load alternative flights.");
      setShowChangeFlightModal(false);
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleSelectNewFlight = async (selectedLeg) => {
      if(!flightToUpdate || !selectedLeg) return;
      
      setBtnLoading(true);
      const displayLeg = selectedLeg.dcDisplayLegs[0];
      const priceDetail = selectedLeg.dcPriceDetails[0];

      // Create updated object
      const updatedFlight = {
          ...flightToUpdate,
          airline: displayLeg.airlineData.airline,
          flightNumber: `${displayLeg.airlineData.airlineCode}-${displayLeg.airlineData.flightNumber}`,
          departureDateTime: displayLeg.startSegment.departureDateTime,
          arrivalDateTime: displayLeg.endSegment.arrivalDateTime,
          price: priceDetail.totalPrice
          // Keep Cities same as search was based on them
      };

      // Update Local List
      const newList = lists.map(x => x.packageFlightBookingId === flightToUpdate.packageFlightBookingId ? updatedFlight : x);
      
      const success = await saveHolidayData(newList);
      if(success) {
          setShowChangeFlightModal(false);
          setCurrentlySelectedFlightInModal(null);
      }
      setBtnLoading(false);
  };

  // --- FORM UI HELPERS (Same as Reference) ---
  const handleAddNewClick = () => { resetForm(); setIsFormVisible(true); };
  const handleCancel = () => { resetForm(); setIsFormVisible(false); };
  
  const showEditDetails = (data) => {
      const formatted = { 
          ...data, 
          departureDateTime: moment(data.departureDateTime).format("YYYY-MM-DDTHH:mm"),
          arrivalDateTime: moment(data.arrivalDateTime).format("YYYY-MM-DDTHH:mm")
      };
      setValues(formatted);
      setIsFormVisible(true);
  };
  
  const resetForm = () => { setValues(initialFieldValues); setErrors({}); };
  
  const handleInputChange = (e) => { 
      const { name, value } = e.target; 
      setValues({ ...values, [name]: value });
      
      if (name === "airline") {
          if(value) {
            setSuggestions(airlinesData.filter(a => a.name.toLowerCase().includes(value.toLowerCase())));
            setIsSuggestionsVisible(true);
          } else {
             setSuggestions([]); setIsSuggestionsVisible(false);
          }
      }
      if ((name === "departureCity" || name === "arrivalCity") && value.length > 2) {
          const filtered = airportsData.filter(a => a.cityName.toLowerCase().includes(value.toLowerCase()) || a.airportCode.toLowerCase().includes(value.toLowerCase()) || a.airportName.toLowerCase().includes(value.toLowerCase())).slice(0,7);
          name === "departureCity" ? setDepartureSuggestions(filtered) : setArrivalSuggestions(filtered);
      } else if(name === "departureCity" || name === "arrivalCity") {
          name === "departureCity" ? setDepartureSuggestions([]) : setArrivalSuggestions([]);
      }
  };
  
  const handleAirportSelect = (field, item) => {
      setValues({...values, [field]: `${item.cityName} (${item.airportCode})`});
      field === 'departureCity' ? setDepartureSuggestions([]) : setArrivalSuggestions([]);
  };

  const validate = () => {
      let temp = {};
      temp.airline = !!values.airline;
      temp.flightNumber = !!values.flightNumber;
      temp.departureCity = !!values.departureCity;
      temp.arrivalCity = !!values.arrivalCity;
      temp.departureDateTime = !!values.departureDateTime;
      temp.arrivalDateTime = !!values.arrivalDateTime;
      temp.price = values.price !== "" && !isNaN(values.price);
      setErrors(temp);
      return Object.values(temp).every(x => x);
  };
  
  const applyErrorClass = (field) => errors[field] === false ? " form-control-danger" : "";

  return (
    <div className="PackageFlight-wrapper">
      <div className="card PackageFlight-card">
        <div className="card-body">
          <div className="PackageFlight-header">
            <h4 className="header-title">Flight Bookings</h4>
            {!isFormVisible && (
              <button type="button" className="btn btn-primary PackageFlight-btn-gradient" onClick={handleAddNewClick}>
                <i className="ri-add-line me-1"></i> Add New Flight
              </button>
            )}
          </div>

          {isFormVisible && (
            <div className="PackageFlight-form-container">
                <h5 className="form-title">{values.packageFlightBookingId ? "Edit Flight Details" : "Add Flight Details"}</h5>
                <form onSubmit={handleSubmit} autoComplete="off">
                    <div className="row">
                        <div className="col-lg-3 mb-3">
                            <label className="form-label d-flex align-items-center"><i className="ri-plane-line me-2"></i>Airline</label>
                            <div className="airline-autocomplete-wrapper" ref={airlineInputWrapperRef}>
                                <input type="text" name="airline" value={values.airline} onChange={handleInputChange} className={"form-control "+applyErrorClass('airline')} placeholder="IndiGo" onClick={() => values.airline && setIsSuggestionsVisible(true)}/>
                                {isSuggestionsVisible && suggestions.length > 0 && <ul className="airline-suggestions-list">{suggestions.map(s => <li key={s.code} className="airline-suggestion-item" onClick={() => {setValues({...values, airline: s.name}); setIsSuggestionsVisible(false);}}><div className="airline-name">{s.name}</div><div className="airline-code">{s.code}</div></li>)}</ul>}
                                {errors.airline === false && <div className="validationerror">Please enter a valid airline</div>}
                            </div>
                        </div>
                        <div className="col-lg-3 mb-3">
                            <label className="form-label d-flex align-items-center"><i className="ri-hashtag me-2"></i>Flight No</label>
                            <input type="text" name="flightNumber" value={values.flightNumber} onChange={handleInputChange} className={"form-control "+applyErrorClass('flightNumber')} placeholder="6E-123"/>
                            {errors.flightNumber === false && <div className="validationerror">Please enter flight number</div>}
                        </div>
                        <div className="col-lg-3 mb-3">
                            <label className="form-label d-flex align-items-center"><i className="ri-flight-takeoff-line me-2"></i>Departure</label>
                            <div className="airline-autocomplete-wrapper" ref={departureInputRef}>
                                <input type="text" name="departureCity" value={values.departureCity} onChange={handleInputChange} className={"form-control "+applyErrorClass('departureCity')} placeholder="Delhi (DEL)"/>
                                {departureSuggestions.length > 0 && <ul className="airline-suggestions-list">{departureSuggestions.map(a => <li key={a.airportCode} className="airline-suggestion-item" onClick={() => handleAirportSelect('departureCity', a)}><div className="airline-name">{getCleanCityName(a)} ({a.airportCode})</div><div className="airline-code">{a.airportName}</div></li>)}</ul>}
                            </div>
                              {errors.departureCity === false && <div className="validationerror">Please select departure city</div>}
                 
                        </div>
                        <div className="col-lg-3 mb-3">
                            <label className="form-label d-flex align-items-center"><i className="ri-flight-land-line me-2"></i>Arrival</label>
                            <div className="airline-autocomplete-wrapper" ref={arrivalInputRef}>
                                <input type="text" name="arrivalCity" value={values.arrivalCity} onChange={handleInputChange} className={"form-control "+applyErrorClass('arrivalCity')} placeholder="Mumbai (BOM)"/>
                                {arrivalSuggestions.length > 0 && <ul className="airline-suggestions-list">{arrivalSuggestions.map(a => <li key={a.airportCode} className="airline-suggestion-item" onClick={() => handleAirportSelect('arrivalCity', a)}><div className="airline-name">{getCleanCityName(a)} ({a.airportCode})</div><div className="airline-code">{a.airportName}</div></li>)}</ul>}
                            </div>
                             {errors.arrivalCity === false && <div className="validationerror">Please select arrival city</div>}
                        </div>
                        <div className="col-lg-3 mb-3"><label className="form-label d-flex align-items-center"><i className="ri-calendar-event-line me-2"></i>Departure Date</label><input type="datetime-local" name="departureDateTime" value={values.departureDateTime} onChange={handleInputChange} className={"form-control "+applyErrorClass('departureDateTime')}/>{errors.departureDateTime === false && <div className="validationerror">Please enter a valid departure date</div>}</div>
                        <div className="col-lg-3 mb-3"><label className="form-label d-flex align-items-center"><i className="ri-calendar-check-line me-2"></i>Arrival Date</label><input type="datetime-local" name="arrivalDateTime" value={values.arrivalDateTime} onChange={handleInputChange} className={"form-control "+applyErrorClass('arrivalDateTime')}/>{errors.arrivalDateTime === false && <div className="validationerror">Please enter a valid arrival date</div>}</div>
                        <div className="col-lg-3 mb-3">
                            <label className="form-label d-flex align-items-center"><i className="ri-shield-star-line me-2"></i>Class</label>
                            <select name="cabinClass" value={values.cabinClass} onChange={handleInputChange} className="form-select">
                                <option value="Economy">Economy</option><option value="Business">Business</option>
                            </select>
                        </div>
                        <div className="col-lg-3 mb-3"><label className="form-label d-flex align-items-center"><i className="ri-money-dollar-circle-line me-2"></i>Price</label><input type="number" name="price" value={values.price} onChange={handleInputChange} className={"form-control "+applyErrorClass('price')}/> {errors.price === false && <div className="validationerror">Please enter a valid price</div>}</div>
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-2">
                        <button type="button" className="btn btn-light" onClick={handleCancel}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={btnLoading}>{btnLoading ? "Saving..." : (values.packageFlightBookingId ? "Update" : "Submit")}</button>
                    </div>  
                </form>
            </div>
          )}

          <div className="table-responsive mt-4">
            <table className="table PackageFlight-table">
              <thead className="PackageFlight-table-header">
                <tr><th>S No.</th><th>Airline</th><th>Flight</th><th>Departure</th><th>Arrival</th><th>Class</th><th>Price</th><th className="text-center">Actions</th></tr>
              </thead>
              <tbody>
                {lists.length > 0 ? lists.map((v, i) => (
                    <tr key={v.packageFlightBookingId || i} className="PackageFlight-table-row">
                        <td>{i + 1}</td>
                        <td>{v.airline}</td>
                        <td>{v.flightNumber}</td>
                        <td><div className="fw-medium">{v.departureCity}</div><div className="datetime-text">{moment(v.departureDateTime).format("DD MMM YYYY, HH:mm")}</div></td>
                        <td><div className="fw-medium">{v.arrivalCity}</div><div className="datetime-text">{moment(v.arrivalDateTime).format("DD MMM YYYY, HH:mm")}</div></td>
                        <td>{v.cabinClass}</td>
                        <td>{v.price}</td>
                        <td className="text-center">
                            <div className="d-flex gap-2 justify-content-center">
                                {/* Only show Change Flight if search is relevant, usually always kept for manual corrections via search */}
                                <button className="PackageFlight-action-btn change" title="Change Flight" onClick={() => handleChangeFlightClick(v)} style={{ backgroundColor: "#ffc107", color: "#fff", borderRadius: "50%", width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><i className="ri-swap-line" /></button>
                                <button className="PackageFlight-action-btn edit" onClick={() => showEditDetails(v)}><i className="ri-pencil-fill" /></button>
                                <button className="PackageFlight-action-btn delete" onClick={() => { setItemToDelete(v.packageFlightBookingId); setShowDeleteModal(true); }}><i className="ri-delete-bin-fill" /></button>
                            </div>
                        </td>
                    </tr>
                )) : <tr><td colSpan="8" className="text-center p-4"><div className="PackageFlight-empty-state"><i className="ri-plane-line empty-icon"></i><h5>No Flights Added Yet</h5></div></td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <DeleteAlert show={showDeleteModal} handleClose={() => setShowDeleteModal(false)} handleDelete={handleDelete} />
      
      <ChangeFlightModal
        show={showChangeFlightModal}
        onHide={() => setShowChangeFlightModal(false)}
        flights={alternativeFlights}
        isLoading={isModalLoading}
        onSelect={handleSelectNewFlight}
        currentlySelectedFlight={currentlySelectedFlightInModal}
      />
    </div>
  );
};

export default HolidayFlightBooking;